import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "components/common/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const AUTO_SCROLL_MS = 5000;
const FADE_MS = 280;

const DashboardAdBanner = ({ ads: adsProp, ad, onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const ads = useMemo(() => {
    if (Array.isArray(adsProp) && adsProp.length > 0) {
      return adsProp.filter((item) => item?.imageUrl);
    }
    if (ad?.imageUrl) return [ad];
    return [];
  }, [adsProp, ad]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(false);

  const hasMultiple = ads.length > 1;
  const safeIndex = ads.length ? activeIndex % ads.length : 0;
  const activeAd = ads[safeIndex] || null;
  const hasImage = Boolean(activeAd?.imageUrl);

  const imageSrc = isMobile
    ? activeAd?.imageUrlMobile || activeAd?.imageUrl
    : activeAd?.imageUrl;

  useEffect(() => {
    setActiveIndex(0);
  }, [ads.length]);

  useEffect(() => {
    const onVisibility = () => {
      setIsTabHidden(document.visibilityState === "hidden");
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (
      !hasMultiple ||
      isPaused ||
      isTabHidden ||
      prefersReducedMotion
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % ads.length);
    }, AUTO_SCROLL_MS);

    return () => window.clearInterval(timer);
  }, [
    hasMultiple,
    isPaused,
    isTabHidden,
    prefersReducedMotion,
    ads.length,
  ]);

  const goTo = useCallback(
    (index, event) => {
      event?.stopPropagation?.();
      if (!ads.length) return;
      setActiveIndex(((index % ads.length) + ads.length) % ads.length);
    },
    [ads.length],
  );

  const handleActivate = useCallback(() => {
    if (hasImage && activeAd) {
      onClick?.(activeAd);
    }
  }, [hasImage, activeAd, onClick]);

  if (!ads.length) {
    return (
      <Box
        sx={{
          flex: 1,
          borderRadius: "16px",
          overflow: "hidden",
          minHeight: { xs: 120, md: 160, lg: 180 },
          border: "1px solid #E5E7EB",
          bgcolor: "#F9FAFB",
        }}
      />
    );
  }

  return (
    <Box
      onClick={hasImage ? handleActivate : undefined}
      role={hasImage ? "button" : undefined}
      tabIndex={hasImage ? 0 : undefined}
      aria-roledescription={hasMultiple ? "carousel" : undefined}
      aria-label={hasMultiple ? "Promotional ads" : activeAd?.altText}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onKeyDown={
        hasImage
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleActivate();
              }
              if (hasMultiple && event.key === "ArrowRight") {
                event.preventDefault();
                goTo(safeIndex + 1);
              }
              if (hasMultiple && event.key === "ArrowLeft") {
                event.preventDefault();
                goTo(safeIndex - 1);
              }
            }
          : undefined
      }
      sx={{
        position: "relative",
        flex: 1,
        borderRadius: "16px",
        overflow: "hidden",
        cursor: hasImage ? "pointer" : "default",
        minHeight: { xs: 120, md: 160, lg: 180 },
        border: "1px solid #E5E7EB",
        bgcolor: "#F9FAFB",
        ...(hasImage ? { "&:hover": { opacity: 0.98 } } : {}),
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 350,
        }}
      >
        {ads.map((item, index) => {
          const src = isMobile
            ? item.imageUrlMobile || item.imageUrl
            : item.imageUrl;
          const isActive = index === safeIndex;

          return (
            <Box
              key={item.adId || `ad-${index}`}
              component="img"
              src={src}
              alt={item.altText || "Advertisement"}
              aria-hidden={!isActive}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                opacity: isActive ? 1 : 0,
                transition: prefersReducedMotion
                  ? "none"
                  : `opacity ${FADE_MS}ms ease`,
                pointerEvents: isActive ? "auto" : "none",
              }}
            />
          );
        })}
      </Box>

      {hasMultiple ? (
        <Box
          component="div"
          role="tablist"
          aria-label="Ad slides"
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 0.75,
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          {ads.map((item, index) => {
            const isActive = index === safeIndex;
            return (
              <Box
                key={`dot-${item.adId || index}`}
                component="button"
                type="button"
                role="tab"
                aria-label={`Go to ad ${index + 1}`}
                aria-selected={isActive}
                onClick={(event) => goTo(index, event)}
                sx={{
                  pointerEvents: "auto",
                  width: isActive ? 18 : 8,
                  height: 8,
                  p: 0,
                  border: 0,
                  borderRadius: 999,
                  cursor: "pointer",
                  bgcolor: isActive
                    ? "#122DB2"
                    : "rgba(17, 24, 39, 0.28)",
                  transition: prefersReducedMotion
                    ? "none"
                    : "width 200ms ease, background-color 200ms ease",
                  "&:hover": {
                    bgcolor: isActive
                      ? "#0E28AD"
                      : "rgba(17, 24, 39, 0.45)",
                  },
                }}
              />
            );
          })}
        </Box>
      ) : null}

      {/* Visually hidden current image for screen readers when fading */}
      {hasImage && hasMultiple ? (
        <Box
          component="span"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
          }}
        >
          {imageSrc}
        </Box>
      ) : null}
    </Box>
  );
};

export default DashboardAdBanner;
